import { categoryByLabel, descriptionsForCategory, tagsForCategory } from "./postTaxonomy";
import { CATEGORY_POSTING_EXAMPLES } from "./categoryPostingExamples";
import { itemDescriptionInformation } from "./itemDescriptionInformation";
import { ITEM_POSTING_EXAMPLES } from "./itemPostingExamples";

export type PostingExamples = {
  title: string;
  description: string;
  photos: string;
  previousPhotos: string;
  descriptionSuggestions?: string[];
  inclusionSuggestions?: string[];
  requirementSuggestions?: string[];
  tags?: string;
  previousWork?: string;
  whatsIncluded?: string;
  requirements?: string;
  rfqLineItems?: { quantityName: string; lumpName: string; quantity: string; unit: string; specifications: string };
};
type PostingExampleContext = { itemKind?: string; itemType?: string; requestType?: string; category?: string };

const EXAMPLES: Record<string, PostingExamples> = {
  job: {
    title: "e.g. Full-time electrician for office maintenance",
    description: "e.g. Maintain lighting and electrical panels at our Pasig office, Monday–Friday. Applicants should have 2 years of maintenance experience and be able to read wiring diagrams.",
    photos: "Examples: the workplace, equipment used in the role, or the team’s work area.",
    previousPhotos: "",
  },
  service: {
    title: "e.g. Custom kitchen cabinet installation",
    description: "e.g. We measure, fabricate and install kitchen cabinets in Metro Manila. The service includes a site visit and installation; countertop work is quoted separately.",
    photos: "Examples: a completed cabinet installation, your workshop, or equipment used for your service.",
    previousPhotos: "Examples: before-and-after photos of a completed kitchen, fitted cabinets, or finished repairs.",
  },
  "company-service": {
    title: "e.g. Commercial air-conditioning maintenance by our team",
    description: "e.g. Our company provides scheduled air-conditioning maintenance for offices in Metro Manila. A team handles cleaning, inspection and a service report; replacement parts are quoted separately.",
    photos: "Examples: your company team at work, service equipment, or a completed maintenance visit.",
    previousPhotos: "Examples: completed company service projects, before-and-after results, or installations by your team.",
  },
  project: {
    title: "e.g. Renovate a 60 m² office in Quezon City",
    description: "e.g. Renovate a 60 m² office with new partitions, lighting and repainting. Include labor and materials in your bid. The site is available for inspection before bidding.",
    photos: "Examples: the current site, rooms to renovate, access points, or the area covered by the project.",
    previousPhotos: "",
  },
  rfq: {
    title: "e.g. Quotation for office supplies or maintenance services",
    description: "e.g. Request quotations for our Pasig office. Select Services or Items, list the scope or specifications, and ask suppliers to include pricing and lead time.",
    photos: "Examples: the required product, reference specifications, or the work area for the requested service.",
    previousPhotos: "",
    rfqLineItems: { quantityName: "Product or service name", lumpName: "Describe the complete scope", quantity: "Quantity", unit: "Unit", specifications: "Specifications or scope details" },
  },
  "rfq-items": {
    title: "e.g. Supply 100 bags of cement for a Pasig site",
    description: "e.g. We need 100 bags of 40 kg Portland cement delivered to Pasig. Quote the unit price, delivery charge and lead time, and state the available brand.",
    photos: "Examples: the required product, a reference design, or the site where supplies will be used.",
    previousPhotos: "Examples: a similar completed installation or a reference finish showing what you need.",
    descriptionSuggestions: ["Please include the brand, specifications and unit price.", "Quote delivery charges and lead time separately.", "State available stock and minimum order quantity."],
    rfqLineItems: { quantityName: "Item (e.g. Portland cement)", lumpName: "Item (e.g. Commercial lot, delivery van fleet)", quantity: "e.g. 100", unit: "Unit (e.g. bag)", specifications: "e.g. 40 kg bags, preferred brand and delivery location" },
  },
  "rfq-services": {
    title: "e.g. Quotation for office air-conditioning maintenance",
    description: "e.g. We need cleaning and inspection of 6 split-type air conditioners at our Pasig office. Quote labor, materials and any replacement parts separately, with your proposed schedule.",
    photos: "Examples: the units needing service, the work area, or reference photos of the requested result.",
    previousPhotos: "",
    descriptionSuggestions: ["Please quote labor and materials separately.", "Include your proposed schedule and completion time.", "State the warranty and any exclusions."],
    rfqLineItems: { quantityName: "Service (e.g. Air-conditioner cleaning)", lumpName: "Scope (e.g. Office air-conditioning maintenance)", quantity: "e.g. 6", unit: "Unit (e.g. unit, visit)", specifications: "e.g. Cleaning and inspection of split-type units" },
  },
  company: {
    title: "e.g. Mabini Construction Services",
    description: "e.g. We provide residential renovation and electrical maintenance in Metro Manila, with a crew of 8 and a workshop in Pasig.",
    photos: "Examples: your office, workshop, team, or completed company projects.",
    previousPhotos: "",
  },
  item: {
    title: "e.g. Used cordless drill – 18 V, with battery",
    description: "e.g. 18 V cordless drill in working condition, used for 1 year. Includes one battery, charger and carrying case. Minor scratches on the handle.",
    photos: "Example: a clear photo of the actual item showing its condition and included accessories.",
    previousPhotos: "",
    descriptionSuggestions: ["Sale inclusions and exclusions: ", "Available for inspection before purchase.", "Pickup or delivery arrangements can be discussed."],
  },
};

const ITEM_EXAMPLES: Record<string, Partial<PostingExamples>> = {
  electrical: {
    title: "e.g. 20 A circuit breakers – unused stock",
    description: "e.g. Unused 20 A circuit breakers in original packaging. State the brand, model, pole count, voltage rating, quantity and price per piece.",
    photos: "Example: the actual electrical components, rating labels and packaging.",
  },
  plumbing_sanitary: {
    title: "e.g. 1-inch PVC pipes – 3 m lengths",
    description: "e.g. Unused 1-inch PVC pipes in 3 m lengths. State the brand, pressure rating, available quantity, price per length and pickup arrangements.",
    photos: "Example: the actual pipes or fittings, dimensions and product labels.",
  },
  safety_ppe: {
    title: "e.g. New construction safety helmets – adjustable fit",
    description: "e.g. Unused adjustable safety helmets in sealed packaging. State the brand, model, stated safety rating, manufacturing date, quantity and price per helmet.",
    photos: "Example: the actual protective equipment, labels and packaging.",
  },
  formwork_scaffold: {
    title: "e.g. Used steel scaffolding frames with braces",
    description: "e.g. Steel scaffolding frames with cross braces for sale. State the dimensions, number of frames, included parts, visible wear and collection arrangements.",
    photos: "Example: the actual frames, braces, connections and visible condition.",
  },
  fixtures_finishes: {
    title: "e.g. 60 × 60 cm porcelain floor tiles – unopened boxes",
    description: "e.g. Unopened boxes of 60 × 60 cm porcelain floor tiles. State the brand, color, finish, batch, coverage per box, available quantity and price per box.",
    photos: "Example: the actual finish, box labels, dimensions and batch details.",
  },
  site_facilities: {
    title: "e.g. Used portable site office – 20 ft",
    description: "e.g. 20 ft portable site office with a door, windows and electrical fittings. Describe its dimensions, condition, included fixtures and transport or loading arrangements.",
    photos: "Example: the actual facility, interior, exterior and included fixtures.",
  },
  surplus_salvaged: {
    title: "e.g. Salvaged steel beams – assorted lengths",
    description: "e.g. Salvaged steel beams in assorted lengths. State the sizes, available quantity or weight, visible rust or damage, price basis and pickup arrangements.",
    photos: "Example: the actual surplus stock, dimensions and visible wear or damage.",
  },
  business: {
    title: "e.g. Operating cabinet-making business for sale in Pasig",
    description: "e.g. Operating cabinet-making workshop for sale. The asking price includes the listed machines and stock. Describe what is included, the premises arrangement and any exclusions.",
    photos: "Example: the business premises, workshop, or main equipment included in the sale.", previousPhotos: "",
    descriptionSuggestions: ["Included equipment and inventory: ", "Premises arrangements are subject to discussion.", "Viewing and sale discussions by appointment."],
  },
  property: {
    title: "e.g. 80 m² commercial office for sale in Makati",
    description: "e.g. 80 m² commercial office with 2 rooms, a pantry and one parking slot. Describe the floor, access, current occupancy and features included in the asking price.",
    photos: "Example: the actual exterior or interior showing the space offered for sale.", previousPhotos: "",
    descriptionSuggestions: ["Viewing by appointment.", "Sale inclusions and exclusions: ", "Access details are included in the description."],
  },
  material: {
    title: "e.g. 40 kg Portland cement bags for sale",
    description: "e.g. Unopened 40 kg Portland cement bags, stored in a dry warehouse. State the brand, available quantity, price per bag and pickup or delivery arrangements.",
    photos: "Example: the actual stock, packaging label, brand and specifications.", previousPhotos: "",
    descriptionSuggestions: ["Available quantity and price per unit are listed above.", "Unused stock stored indoors.", "Brand and specifications are shown in the photo."],
  },
  vehicle_transport: {
    title: "e.g. Used delivery van – diesel, manual transmission",
    description: "e.g. Diesel delivery van with manual transmission and 85,000 km mileage. Describe its year, maintenance history, registration documents, known issues and inspection arrangements.",
    photos: "Example: the actual vehicle, exterior condition, cabin or cargo space.", previousPhotos: "",
    descriptionSuggestions: ["Inspection by appointment.", "Service records available on request.", "Accessories included: "],
  },
  heavy_machinery: {
    title: "e.g. Used 3-ton forklift with service records",
    description: "e.g. 3-ton diesel forklift with 2,400 operating hours. State the brand, model, lifting capacity, maintenance history, known issues and loading arrangements.",
    photos: "Example: the actual machine, identification plate, attachments and condition.", previousPhotos: "",
    descriptionSuggestions: ["Available for inspection before purchase.", "Service records available on request.", "Included attachments: "],
  },
};

const GENERAL_EXAMPLES: PostingExamples = { title: "Enter a title for this listing", description: "Describe what you are posting, its specifications or scope, and any relevant terms.", photos: "Example: a clear photo relevant to this listing.", previousPhotos: "" };

function categoryExamples(type: string, context: PostingExampleContext): Partial<PostingExamples> {
  const category = categoryByLabel(context.category || "");
  const example = category && CATEGORY_POSTING_EXAMPLES[category.key];
  if (!example) return {};
  if (type === "job") return {
    title: `e.g. ${example.jobTitle}`,
    description: `e.g. ${example.jobDuties}`,
    photos: `Examples: the workplace, ${example.workPhotos}.`,
    tags: `e.g. ${example.skill} — press Enter`,
  };
  if (type === "service" || type === "company-service") return {
    title: `e.g. ${example.serviceTitle}${type === "company-service" ? " by our company team" : ""}`,
    description: `e.g. ${type === "company-service" ? "Our company team can" : "I can"} ${example.serviceScope}`,
    photos: `Examples: ${type === "company-service" ? "your company team at work, " : ""}${example.workPhotos}.`,
    previousPhotos: `Examples: completed ${example.serviceTitle.toLowerCase()}, before-and-after results or relevant portfolio work.`,
    tags: `e.g. ${tagsForCategory(context.category || "").slice(0, 2).join(", ")} — press Enter`,
    previousWork: `e.g. Completed ${example.serviceTitle.toLowerCase()} for a client. Describe the scope, deliverables and results.`,
    whatsIncluded: `e.g.\n• ${example.inclusions.split("\n").join("\n• ")}`,
    requirements: `e.g. ${example.clientRequirements}`,
    inclusionSuggestions: example.inclusions.split("\n"),
    requirementSuggestions: [example.clientRequirements],
  };
  if (type === "project") return {
    title: `e.g. ${example.serviceTitle} project`,
    description: `e.g. We need a provider to ${example.projectScope}`,
    photos: `Examples: ${example.projectPhotos}.`,
  };
  if (type === "rfq" && context.requestType === "services") return {
    title: `e.g. Quotation for ${example.serviceTitle.toLowerCase()}`,
    description: `e.g. We need quotations to ${example.projectScope} Include pricing, proposed timing and exclusions.`,
    photos: `Examples: ${example.projectPhotos}.`,
    previousWork: `e.g. Previously arranged ${example.serviceTitle.toLowerCase()}. Describe the earlier scope and what is needed this time.`,
    rfqLineItems: { quantityName: `Service (e.g. ${example.serviceTitle})`, lumpName: `Scope (e.g. ${example.serviceTitle} project)`, quantity: "e.g. 1", unit: "Unit (e.g. service, visit)", specifications: `e.g. ${example.clientRequirements}` },
  };
  if (type === "rfq" && context.requestType === "items") return {
    title: `e.g. Quotation for ${example.quantity} ${example.unit} of ${example.itemName}`,
    description: `e.g. We need ${example.quantity} ${example.unit} of ${example.itemName}: ${example.itemSpecifications}. Quote the unit price, availability, delivery charge and lead time.`,
    photos: `Examples: ${example.itemPhotos}.`,
    previousWork: `e.g. Previous order of ${example.itemName}. Describe the earlier specifications and what is needed this time.`,
    rfqLineItems: { quantityName: `Item (e.g. ${example.itemName})`, lumpName: `Complete order (e.g. ${example.itemName})`, quantity: `e.g. ${example.quantity}`, unit: `Unit (e.g. ${example.unit})`, specifications: `e.g. ${example.itemSpecifications}` },
  };
  return {};
}

// [posting.examples-and-suggestions](../../../docs/product/marketplace.md#posting-examples-and-suggestions): guidance follows current type/category and selected item
// ID. Legacy kinds and unknown categories retain type-appropriate fallback copy.
// Details and Review consume the same examples; this helper never changes drafts.
export function postingExamples(type: string, context: PostingExampleContext = {}): PostingExamples {
  if (type === "item") {
    const specific = ITEM_POSTING_EXAMPLES[context.itemType || ""];
    const examples = { ...EXAMPLES.item, ...ITEM_EXAMPLES[context.itemKind || ""], ...specific };
    // Stock/storage and vehicle-service claims from legacy kinds do not fit every
    // subtype (for example fresh produce or wheel sets). Keep optional starters neutral.
    if (specific && context.itemKind !== "property" && context.itemKind !== "business") examples.descriptionSuggestions = EXAMPLES.item.descriptionSuggestions;
    const listGuidance = itemDescriptionInformation(context.itemType || "");
    if (listGuidance) return {
      ...examples,
      description: `${examples.description} ${listGuidance.hint}`,
      descriptionSuggestions: [...listGuidance.suggestions, ...(examples.descriptionSuggestions || []).filter((line) => !line.startsWith("Included ") && !line.startsWith("Sale inclusions"))],
    };
    return examples;
  }
  const base = type === "rfq" ? EXAMPLES[`rfq-${context.requestType}`] || EXAMPLES.rfq : EXAMPLES[type] || GENERAL_EXAMPLES;
  const examples = { ...base, ...categoryExamples(type, context) };
  if (type === "service" || type === "company-service") return { ...examples, descriptionSuggestions: descriptionsForCategory(context.category || "") };
  return examples;
}
